/**
 * ⭐⭐ **A MATRÍCULA ENTRA PELA FICHA — PRD §76 e §77, 30/10/2026.**
 *
 * > *"Teremos que ter um caminho só, pode ser algo como: o Hub lê e o GPT/CODEX lê, os dois retornam as informações
 * > para o Hub e o Hub preenche o 'formulario'..."* — Richard, 30/10/2026 (a versão 2, aprovada)
 *
 * **A ficha é o único lugar.** "+ Imóvel" (que mostra quantas matrículas lidas esperam) abre a ficha; na ficha em
 * branco, **"Anexe a matrícula"**, com o que vai e para quem à vista antes do arquivo; anexar já lê (o Hub e o GPT
 * leem; o Hub junta campo a campo, `lib/leitura-combinada.ts`) e a ficha se preenche sozinha. "Lançar" leva à próxima;
 * "Descartar" tira esta. A leitura que falha tenta de novo sozinha; falhando, a ficha daquele PDF abre em branco, com o
 * motivo. A matrícula já lançada nesta parte abre como "completar o lançamento" (só o vazio). A chave "este caso não
 * vai a IA de fora" mora no cadastro do caso (§77, decisão dele). A fila (`lib/fila-de-matriculas.ts`) é o motor, só
 * na memória da aba.
 *
 * ⛔ O que não mudou: "lido" não é "lançado" (quem lança é ele, um por vez); o papel sem o documento nem o nome do
 * investigado só se lança com a caixinha "Confirmo que este imóvel é desta parte"; sem nome de pessoa nas linhas (o
 * arquivo é "Arquivo 7" até ele pedir o nome).
 */
import { useEffect, useState, type ReactNode } from 'react';
import type { ClassificacaoDoAtivo, MatriculaProposta } from '../lib/api';
import { acrescentar, atualizarCota, confirmarDaParte, MAXIMO_ABERTOS, POR_VEZ, usarCota, usarFila, type ItemDaFila } from '../lib/fila-de-matriculas';
import { avisar } from './Avisos';
import { Icone } from './Icone';

/** Um imóvel já lançado no caso, para dizer "já lançada" sem perguntar ao servidor. */
export type LancadoNoCaso = {
  ativoId: string;
  parteId: string;
  numero: string | null;
  cartorio: string | null;
  cidade: string | null;
  uf: string | null;
};

/** `R-2/02.345` e `2.345` são o mesmo número: só os algarismos, sem zero à esquerda. */
export function numeroDaMatricula(t: string | null | undefined): string | null {
  const d = (t ?? '').replace(/\D/g, '').replace(/^0+/, '');
  return d || null;
}

const comparavel = (t: string | null | undefined) =>
  (t ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');

/**
 * ⚠️ **Mesmo número não basta: cada cartório tem a sua numeração.** A cidade decide quando os dois lados a têm; senão,
 * o cartório (um contido no outro, porque a ficha guarda o que ele digitou e a leitura o que o papel diz).
 */
export function mesmoLugar(
  a: { cartorio: string | null; cidade: string | null; uf: string | null },
  b: { cartorio: string | null; cidade: string | null; uf: string | null },
): 'sim' | 'nao' | 'sem-dado' {
  const [ca, cb] = [comparavel(a.cidade), comparavel(b.cidade)];
  if (ca && cb) {
    const [ua, ub] = [comparavel(a.uf), comparavel(b.uf)];
    return ca === cb && (!ua || !ub || ua === ub) ? 'sim' : 'nao';
  }
  const [ra, rb] = [comparavel(a.cartorio), comparavel(b.cartorio)];
  if (ra && rb) return ra.includes(rb) || rb.includes(ra) ? 'sim' : 'nao';
  return 'sem-dado';
}

/* ── O que cada leitura pede (o que era a linha da fila do §70) ─────────────── */

export type SituacaoDaLeitura = 'na-fila' | 'lendo' | 'pronto' | 'a-confirmar' | 'ja-lancada' | 'erro' | 'repetido' | 'lancado';

export type AvaliacaoDaLeitura = {
  situacao: SituacaoDaLeitura;
  avisos: string[];
  /** O lançamento desta parte com a mesma matrícula (para "Abrir o lançamento"). */
  lancamento: string | null;
};

const lugarDe = (item: ItemDaFila) => ({ cartorio: item.leitura!.cartorio, cidade: item.leitura!.imovel.cidade, uf: item.leitura!.imovel.uf });

export function avaliarLeitura(item: ItemDaFila, itens: ItemDaFila[], lancados: LancadoNoCaso[], investigadoId: string): AvaliacaoDaLeitura {
  const avisos: string[] = [];
  if (item.estado !== 'pronto' || !item.leitura) return { situacao: item.estado, avisos, lancamento: null };
  let situacao: SituacaoDaLeitura = item.leitura.reconhecidoPor === 'nenhum' && !item.confirmado ? 'a-confirmar' : 'pronto';
  let lancamento: string | null = null;
  const numero = numeroDaMatricula(item.leitura.matricula);
  if (numero) {
    for (const l of lancados.filter((x) => numeroDaMatricula(x.numero) === numero)) {
      const m = mesmoLugar(lugarDe(item), l);
      if (l.parteId === investigadoId) {
        if (m === 'sim') {
          situacao = 'ja-lancada';
          lancamento = l.ativoId;
        } else avisos.push('Número igual ao de um imóvel já lançado nesta parte, em outro cartório ou sem cartório: confira.');
      } else if (m === 'sim') {
        avisos.push('Já lançada em outra parte do caso: pode ser copropriedade, confira.');
      }
    }
    if (situacao !== 'ja-lancada') {
      const irma = itens.find(
        (x) =>
          x.id !== item.id &&
          (x.estado === 'pronto' || x.estado === 'lancado') &&
          x.leitura &&
          numeroDaMatricula(x.leitura.matricula) === numero &&
          mesmoLugar(lugarDe(item), lugarDe(x)) !== 'nao',
      );
      if (irma) avisos.push(`Mesmo imóvel do Arquivo ${irma.ordem}${irma.estado === 'lancado' ? ', já lançado agora' : ''}: lance um e descarte o outro.`);
    }
  }
  return { situacao, avisos, lancamento };
}

/**
 * A próxima leitura a abrir na ficha: as prontas primeiro, depois as que pedem a confirmação da parte, as já lançadas
 * (que abrem como "completar o lançamento") e, por fim, as que não foram lidas (a ficha em branco, com o motivo).
 */
export function proximaLeitura(itens: ItemDaFila[], lancados: LancadoNoCaso[], investigadoId: string, exceto?: string): ItemDaFila | null {
  const ordem: SituacaoDaLeitura[] = ['pronto', 'a-confirmar', 'ja-lancada', 'erro'];
  const candidatas = itens
    .filter((i) => i.id !== exceto && (i.estado === 'pronto' || i.estado === 'erro'))
    .map((i) => ({ i, s: avaliarLeitura(i, itens, lancados, investigadoId).situacao }))
    .sort((a, b) => ordem.indexOf(a.s) - ordem.indexOf(b.s) || a.i.ordem - b.i.ordem);
  return candidatas[0]?.i ?? null;
}

/** ⭐ §77: as contas do "+ Imóvel": quantas esperam a ficha (lidas ou não lidas) e quantas ainda leem. */
export function contarLeituras(itens: ItemDaFila[]): { esperam: number; lendo: number } {
  return {
    esperam: itens.filter((i) => i.estado === 'pronto' || i.estado === 'erro').length,
    lendo: itens.filter((i) => i.estado === 'lendo' || i.estado === 'na-fila').length,
  };
}

/**
 * A proposta que a ficha recebe. ⭐ "O que conferir" vai inteiro: o que o fiscal e o leitor avisaram junto do que a
 * proposta já trazia.
 */
export function propostaDaLeitura(item: ItemDaFila): MatriculaProposta | null {
  const l = item.leitura;
  if (!l?.proposta) return null;
  return { ...l.proposta, ressalvas: [...new Set([...l.ressalvas, ...l.proposta.ressalvas])] };
}

/** A matrícula já lançada nesta parte que não pôde abrir como "completar" (o lançamento não está na tela): trava. */
export function bloqueioDaLeitura(a: AvaliacaoDaLeitura): string | null {
  if (a.situacao === 'ja-lancada') return 'Esta matrícula já está lançada nesta parte: abra o lançamento ou descarte esta leitura.';
  return null;
}

/** ⭐ §77: a caixinha ao lado do "Lançar", só quando o papel não traz o documento nem o nome do investigado. */
export type ConfirmacaoDaParte = { texto: string; feita: boolean; aoMudar: (sim: boolean) => void };

const SUGESTAO_CURTA: Partial<Record<ClassificacaoDoAtivo, string>> = {
  imovel_livre: 'sem ônus vivo',
  imovel_onerado: 'com ônus',
  imovel_transmitido: 'transmitido',
  imovel_matricula_encerrada: 'matrícula encerrada',
  imovel_de_terceiro: 'em nome de terceiro',
};

/* ── As peças pequenas ──────────────────────────────────────────────────────── */

/** "Arquivo 7", com o olho que mostra o nome original (que costuma trazer o nome da pessoa). */
function NomeDoArquivo({ item }: { item: ItemDaFila }) {
  const [visivel, setVisivel] = useState(false);
  return (
    <span className="fila-arquivo">
      <span className="dado">Arquivo {item.ordem}</span>
      {visivel && <span className="fila-nome-original"> · {item.nome}</span>}
      <button
        type="button"
        className="fila-olho"
        aria-pressed={visivel}
        aria-label={visivel ? 'Esconder o nome do arquivo' : 'Mostrar o nome do arquivo'}
        onClick={() => setVisivel((v) => !v)}
      >
        <Icone nome="olho" tamanho={14} />
      </button>
    </span>
  );
}

/** O relógio do "lendo há 20 s", que anda só enquanto há leitura. */
function useAgora(ligado: boolean): number {
  const [agora, setAgora] = useState(() => Date.now());
  useEffect(() => {
    if (!ligado) return;
    const t = setInterval(() => setAgora(Date.now()), 1000);
    return () => clearInterval(t);
  }, [ligado]);
  return agora;
}

/** ⭐ §77: o que está lendo, numa linha (sem botão): a ficha se preenche sozinha quando a primeira volta. */
function EstadoDasLeituras({ itens, podeAbrir }: { itens: ItemDaFila[]; podeAbrir: boolean }) {
  const lendo = itens.filter((i) => i.estado === 'lendo' || i.estado === 'na-fila').sort((a, b) => a.ordem - b.ordem);
  const esperam = itens.filter((i) => i.estado === 'pronto' || i.estado === 'erro').length;
  const agora = useAgora(lendo.some((i) => i.estado === 'lendo'));
  if (!lendo.length && !esperam) return null;
  return (
    <p className="anexo-lendo-linha" aria-live="polite">
      {lendo.length > 0 && (
        <>
          <strong>Lendo…</strong>{' '}
          {lendo.map((i, n) => (
            <span key={i.id}>
              {n > 0 && ' · '}
              <span className="dado">Arquivo {i.ordem}</span>{' '}
              {i.estado === 'na-fila' ? 'na fila' : i.comOGpt ? 'com o GPT' : 'com o Hub'}
              {i.estado === 'lendo' && i.comecouEm ? <> há {Math.max(0, Math.round((agora - i.comecouEm) / 1000))} s</> : null}
            </span>
          ))}
          {podeAbrir ? '. A ficha se preenche sozinha quando a primeira voltar.' : '.'}
        </>
      )}
      {esperam > 0 && !podeAbrir && (
        <>
          {lendo.length > 0 && ' '}
          <span className="dado">{esperam}</span> {esperam === 1 ? 'matrícula espera' : 'matrículas esperam'} a vez: {esperam === 1 ? 'ela abre' : 'a próxima abre'} quando você lançar ou fechar esta ficha.
        </>
      )}
    </p>
  );
}

/* ── A faixa do anexo, no alto da ficha em branco ───────────────────────────── */

/**
 * "Anexe a matrícula": o botão, o que vai e para quem e o arrastar. ⭐ O aviso fica ANTES do clique (decisão dele no
 * §76): escolher o arquivo é o "sim". ⭐ §77: a chave do caso saiu daqui (mora no cadastro do caso).
 */
export function AnexarMatricula({ investigadoId, aoAnexar }: { investigadoId: string; aoAnexar?: () => void }) {
  const cota = usarCota();
  useEffect(() => {
    void atualizarCota(investigadoId);
  }, [investigadoId]);
  const soNoHub = cota?.caso?.soNoHub === true;

  async function escolher(arquivos: File[]) {
    if (arquivos.length === 0) return;
    const { aceitos, deFora, foraPorVez, repetidos } = await acrescentar(investigadoId, arquivos);
    if (repetidos.length > 0) {
      avisar(`${repetidos.length === 1 ? 'Um arquivo já estava' : `${repetidos.length} arquivos já estavam`} na leitura (o mesmo do Arquivo ${repetidos.join(', ')}): ficou de fora.`, 'ruim');
    }
    if (foraPorVez > 0) {
      avisar(`Vão ${POR_VEZ} por vez: ${foraPorVez} ${foraPorVez === 1 ? 'ficou' : 'ficaram'} de fora. Anexe ${foraPorVez === 1 ? 'esse' : 'esses'} depois.`, 'ruim');
    }
    if (deFora > 0) {
      avisar(`${deFora} ${deFora === 1 ? 'PDF ficou' : 'PDFs ficaram'} de fora: cabem ${MAXIMO_ABERTOS} leituras abertas por parte. Lance ou descarte e anexe de novo.`, 'ruim');
    }
    if (aceitos > 0) aoAnexar?.();
  }

  return (
    <div
      className="anexo-matricula"
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        void escolher([...e.dataTransfer.files]);
      }}
    >
      <label className="imv-bt primario">
        <Icone nome="subir" tamanho={14} /> Anexe a matrícula
        <input
          type="file"
          accept="application/pdf"
          multiple
          hidden
          onChange={(e) => {
            const arquivos = [...(e.target.files ?? [])];
            e.target.value = '';
            void escolher(arquivos);
          }}
        />
      </label>
      <p className="anexo-o-que-sai">
        {!cota?.ligada ? (
          <>O Hub lê o PDF aqui dentro; nada sai. Um imóvel por PDF, até {POR_VEZ} por vez (ou arraste para cá).</>
        ) : soNoHub ? (
          <>Este caso está marcado, no cadastro do caso, para não ir a IA de fora: o Hub lê o PDF aqui dentro, e nada sai. Até {POR_VEZ} por vez.</>
        ) : (
          <>
            Ao anexar, o Hub lê o PDF e ele vai também ao GPT (OpenAI), fora do Brasil, pela sua assinatura, só para esta
            leitura; o Hub junta as duas leituras e preenche a ficha, e nada é lançado sem o seu clique. Um imóvel por PDF, até{' '}
            {POR_VEZ} por vez (ou arraste para cá).
          </>
        )}
        {cota?.ligada && !soNoHub && !cota.cota.podeLer && cota.cota.porQue && (
          <span className="fila-aviso"> {cota.cota.porQue} A leitura será só pelo Hub.</span>
        )}
      </p>
    </div>
  );
}

/**
 * ⭐ O alto da ficha que veio de uma leitura: a matrícula, quem leu e como juntou, de quem é o papel e os avisos. Os
 * campos e "O que conferir antes de lançar" estão na ficha; "Descartar" e a caixinha da parte, no pé dela.
 */
export function CabecaDaLeitura({
  item,
  avaliacao,
  completando,
  aoAbrirLancamento,
}: {
  item: ItemDaFila;
  avaliacao: AvaliacaoDaLeitura;
  completando: boolean;
  aoAbrirLancamento: (ativoId: string) => void;
}) {
  const leitura = item.leitura;
  if (!leitura) {
    /* ⭐ §77: a leitura falhou duas vezes: a ficha daquele PDF abre em branco, com o motivo */
    return (
      <div className="anexo-cabeca">
        <p className="ajuda-alerta ajuda-solta">
          Não consegui ler o <NomeDoArquivo item={item} />: {item.erro ?? 'o Hub não reconheceu o papel.'} Preencha à mão,
          olhando o PDF, ou descarte.
        </p>
      </div>
    );
  }
  const dele = leitura.atos.filter((a) => a.papel);
  return (
    <div className="anexo-cabeca">
      <p className="anexo-qual">
        <strong className="dado">Mat. {leitura.matricula ?? 'sem número lido'}</strong>
        {leitura.cartorio && <> · {leitura.cartorio}</>}
        {(leitura.imovel.cidade || leitura.imovel.uf) && <> · {[leitura.imovel.cidade, leitura.imovel.uf].filter(Boolean).join('/')}</>}
        {leitura.classificacaoSugerida && <> · {SUGESTAO_CURTA[leitura.classificacaoSugerida] ?? 'com sugestão'}</>} ·{' '}
        <NomeDoArquivo item={item} />
      </p>
      {completando && (
        <p className="quem-leu">
          <strong>Esta matrícula já está lançada nesta parte:</strong> a leitura completa só o que estava vazio no lançamento,
          e nada do que está lá é apagado.
        </p>
      )}
      {/* Quem leu e como juntou, sem "carimbo de certo" (parecer do Design de 28/10). */}
      {!completando && leitura.pelo === 'codex' && (
        <p className="quem-leu">
          {leitura.porQue ?? 'Lido pelo Hub e pelo GPT.'}{' '}
          <span className="ajuda">Onde os dois concordaram, o campo vem sem marca; sem marca não quer dizer conferido.</span>
        </p>
      )}
      {!completando && leitura.pelo === 'hub' && leitura.soNoHub && (
        <p className="quem-leu quem-leu-reserva">
          <strong>Leitura só do Hub.</strong> {leitura.soNoHub} Não houve segunda leitura: os campos que decidem vêm todos para
          conferir.
        </p>
      )}
      {/* ⛔⛔ Quem é o dono no papel, no TOPO: pendurar o imóvel de um terceiro na parte é o erro mais caro desta seção. */}
      {leitura.reconhecidoPor === 'nome' && (
        <p className="ajuda-alerta ajuda-solta">
          ⚠️ O <strong>CPF/CNPJ não aparece</strong> nesta matrícula: o reconhecimento foi <strong>pelo nome</strong>. Nome não tem
          dígito verificador: confira no papel antes de lançar.
        </p>
      )}
      {leitura.reconhecidoPor === 'nenhum' && !completando && (
        <p className="ajuda-alerta ajuda-solta">
          ⛔ <strong>Nem o documento nem o nome</strong> do investigado foram encontrados neste papel. Pode ser escaneado com
          leitura imperfeita, matrícula antiga sem CPF, ou o PDF de outra pessoa: para lançar, confirme no pé da ficha.
        </p>
      )}
      {avaliacao.situacao === 'ja-lancada' && avaliacao.lancamento && !completando && (
        <p className="ajuda-alerta ajuda-solta">
          Esta matrícula já está lançada nesta parte; nada é gravado por cima.{' '}
          <button type="button" className="imv-bt p" onClick={() => aoAbrirLancamento(avaliacao.lancamento!)}>
            Abrir o lançamento
          </button>
        </p>
      )}
      {avaliacao.avisos.map((a) => (
        <p key={a} className="fila-aviso">
          {a}
        </p>
      ))}
      {leitura.documentos.length > 1 && (
        <p className="ajuda">
          Documentos que aparecem no papel: <span className="dado">{leitura.documentos.join(' · ')}</span>. Confira se há alguém
          aqui que ainda não está no caso.
        </p>
      )}
      {leitura.atos.length > 0 && (
        <details className="outros-atos">
          <summary>
            Os atos lidos (<span className="dado">{leitura.atos.length}</span>
            {dele.length > 0 && <>, {dele.length} com o investigado</>})
          </summary>
          <ul className="atos-lidos">
            {leitura.atos.map((a) => (
              <li key={a.marca + a.data} className={a.efeito === 'informa' ? 'ato-informativo' : undefined}>
                <span className="dado">{a.marca}</span> {a.data && <span className="dado">{a.data.split('-').reverse().join('/')}</span>}{' '}
                {a.natureza ?? <em>tipo não reconhecido</em>}
                {a.papel && (
                  <>
                    {' '}
                    · <strong>{a.papel}</strong>
                  </>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

/**
 * Tudo o que fica no alto da ficha da parte: a leitura que está nela, se houver; senão, o anexo e o que está lendo.
 * ⭐ §77: pronta a primeira leitura e a ficha em branco (`podeAbrir`), ela abre sozinha (`aoAbrir`). `children` recebe
 * o bloqueio e a caixinha da parte, que a ficha põe ao lado do "Lançar".
 */
export function FichaComMatricula({
  investigadoId,
  nomeDaParte,
  lancados,
  naFicha,
  completando,
  podeAbrir,
  aoAbrir,
  aoAnexar,
  aoAbrirLancamento,
  children,
}: {
  investigadoId: string;
  nomeDaParte: string;
  lancados: LancadoNoCaso[];
  naFicha: string | null;
  completando: boolean;
  podeAbrir: boolean;
  aoAbrir: (item: ItemDaFila) => void;
  aoAnexar?: () => void;
  aoAbrirLancamento: (ativoId: string) => void;
  children: (bloqueio: string | null, confirmacao: ConfirmacaoDaParte | null) => ReactNode;
}) {
  const itens = usarFila(investigadoId);
  const item = naFicha ? (itens.find((i) => i.id === naFicha && (i.estado === 'pronto' || i.estado === 'erro')) ?? null) : null;
  const avaliacao = item ? avaliarLeitura(item, itens, lancados, investigadoId) : null;
  const proxima = !naFicha && podeAbrir ? proximaLeitura(itens, lancados, investigadoId) : null;
  useEffect(() => {
    if (proxima) aoAbrir(proxima);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proxima?.id]);
  /* a caixinha só na ficha NOVA: completar um lançamento que já é desta parte não pede confirmação */
  const confirmacao: ConfirmacaoDaParte | null =
    item?.leitura?.reconhecidoPor === 'nenhum' && !completando
      ? {
          texto: `Confirmo que o imóvel da matrícula ${item.leitura.matricula ?? 'sem número'} é de ${nomeDaParte}, mesmo sem o documento nem o nome no papel.`,
          feita: item.confirmado,
          aoMudar: (sim) => confirmarDaParte(investigadoId, item.id, sim),
        }
      : null;
  return (
    <>
      <section className="anexo-na-ficha" aria-label="A matrícula deste imóvel">
        {item && avaliacao ? (
          <CabecaDaLeitura item={item} avaliacao={avaliacao} completando={completando} aoAbrirLancamento={aoAbrirLancamento} />
        ) : (
          <AnexarMatricula investigadoId={investigadoId} aoAnexar={aoAnexar} />
        )}
        <EstadoDasLeituras itens={itens.filter((i) => i.id !== naFicha)} podeAbrir={!naFicha && podeAbrir} />
      </section>
      {children(avaliacao && !completando ? bloqueioDaLeitura(avaliacao) : null, confirmacao)}
    </>
  );
}

/** ⭐ §77: no "+ Imóvel", quantas matrículas esperam a ficha e quantas ainda leem (a fila mora só nesta aba). */
export function ContaDasLeituras({ investigadoId }: { investigadoId: string }) {
  const { esperam, lendo } = contarLeituras(usarFila(investigadoId));
  if (!esperam && !lendo) return null;
  return (
    <span className="imv-conta-leituras" title="Ficam só nesta aba: fechar a aba perde as que ainda não foram lançadas.">
      {esperam > 0 ? `${esperam} ${esperam === 1 ? 'lida' : 'lidas'}` : `lendo ${lendo}`}
    </span>
  );
}
